/**
 * Shared CSS for devtools UI field components (StringDevUIBuilder).
 * Uses --string-dev-* tokens from StringDevStyleSystem.
 *
 * Consumed by any devtool module that uses StringDevUIBuilder.
 * Include via: ensureStringDevtoolsFieldStyles()
 */
export function buildDevtoolsFieldsCSS(): string {
  return `
    [data-string-dev-settings-container] {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 0;
    }

    [data-string-dev-field-row] {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 0;
      min-height: 32px;
      padding: 0 0px 0 8px;
      min-width: 0;
      border: 1px solid var(--string-dev-field-border);
      border-radius: 8px;
      background: var(--string-dev-field-bg);
      overflow: hidden;
    }

    [data-string-dev-field-label] {
      font-size: 12px;
      color: #111111;
      font-family: var(--string-dev-panel-font-family);
      user-select: none;
      white-space: nowrap;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    [data-string-dev-field-input],
    [data-string-dev-field-select] {
      min-height: var(--string-dev-input-height);
      font-size: var(--string-dev-panel-font-size);
      font-family: var(--string-dev-panel-font-family);
      background: var(--string-dev-field-bg);
      border: 1px solid var(--string-dev-field-border);
      border-radius: var(--string-dev-field-radius);
      color: var(--string-dev-panel-text);
      outline: none;
      box-sizing: border-box;
    }

    [data-string-dev-field-input] {
      width: 100%;
      font-size: 12px;
      font-family: var(--string-dev-panel-font-family);
      font-weight: 500;
      color: #111111;
      text-align: center;
    }

    [data-string-dev-field-select] {
      cursor: pointer;
    }

    [data-string-dev-field-input]:focus,
    [data-string-dev-field-select]:focus,
    [data-string-dev-field-unit]:focus {
      border-color: var(--string-dev-focus-color);
    }

    [data-string-dev-field-select] option,
    [data-string-dev-field-unit] option {
      background: #ffffff;
      color: var(--string-dev-panel-text);
    }

    [data-string-dev-field-range-right] {
      display: flex;
      align-items: center;
      gap: 0;
      min-width: 0;
      justify-content: flex-end;
      min-height: 100%;
      border-left: 1px solid rgba(17, 17, 17, 0.15);
    }

    [data-string-dev-number-input],
    [data-string-dev-range-input] {
      display: grid;
      align-items: center;
      width: auto;
      max-width: 100%;
      min-width: 0;
      min-height: 100%;
      border: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
      overflow: hidden;
      box-sizing: border-box;
      justify-self: end;
    }

    [data-string-dev-number-input] {
      grid-template-columns: minmax(0, 75px) 28px;
    }

    [data-string-dev-range-input] {
      grid-template-columns: minmax(0, 40px) auto 28px;
    }

    [data-string-dev-range-input][data-has-unit="false"] {
      grid-template-columns: minmax(0, 75px) 28px;
    }

    [data-string-dev-field-row] > [data-string-dev-number-input],
    [data-string-dev-field-row] > [data-string-dev-range-input],
    [data-string-dev-field-row] > [data-string-dev-select-shell],
    [data-string-dev-field-row] > [data-string-dev-color-shell] {
      min-height: 100%;
      border-left: 1px solid rgba(17, 17, 17, 0.15);
    }

    [data-string-dev-input-value-wrap],
    [data-string-dev-input-unit-wrap] {
      min-width: 0;
      min-height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
    }

    [data-string-dev-input-value-wrap] {
      padding: 0 0px;
    }

    [data-string-dev-input-unit-wrap] {
      border-left: 1px solid rgba(17, 17, 17, 0.15);
      min-width: 32px;
      padding: 0 6px;
    }

    [data-string-dev-stepper-stack] {
      display: grid;
      grid-template-rows: 1fr 1fr;
      min-height: 100%;
      border-left: 1px solid rgba(17, 17, 17, 0.15);
    }

    [data-string-dev-number-input] [data-string-dev-field-input],
    [data-string-dev-range-input] [data-string-dev-field-value],
    [data-string-dev-range-input] [data-string-dev-field-unit] {
      background: transparent;
      border: 0;
      border-radius: 0;
      box-shadow: none;
    }

    [data-string-dev-field-value] {
      width: 100%;
      min-height: calc(var(--string-dev-input-height) - 4px);
      padding: 0;
      font-size: 12px;
      font-family: var(--string-dev-panel-font-family);
      font-weight: 500;
      color: #111111;
      background: transparent;
      border: 0;
      border-radius: 0;
      text-align: center;
      outline: none;
      transition: border-color 0.15s ease, color 0.15s ease;
      box-sizing: border-box;
    }

    [data-string-dev-stepper-btn] {
      width: 100%;
      min-width: 0;
      min-height: 0;
      padding: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: var(--string-dev-panel-text);
      font-size: 12px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, color 0.15s ease;
    }

    [data-string-dev-stepper-btn][data-direction="up"] {
      border-bottom: 1px solid rgba(17, 17, 17, 0.15);
    }

    [data-string-dev-stepper-btn]:hover,
    [data-string-dev-stepper-btn]:focus-visible {
      background: rgba(255, 255, 255, 0.3);
      color: var(--string-dev-panel-text);
      outline: none;
    }

    [data-string-dev-stepper-icon] {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      color: rgba(17, 17, 17, 0.72);
    }

    [data-string-dev-stepper-icon] svg {
      width: 100%;
      height: 100%;
    }

    [data-string-dev-field-value]:focus {
      color: #111111;
    }

    [data-string-dev-field-unit] {
      width: 100%;
      min-height: calc(var(--string-dev-input-height) - 4px);
      padding: 0;
      font-size: 12px;
      font-family: var(--string-dev-panel-font-family);
      font-weight: 500;
      color: var(--string-dev-panel-text);
      background: transparent;
      border: 0;
      border-radius: 0;
      outline: none;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      text-align: center;
      text-align-last: center;
    }

    [data-string-dev-select-shell],
    [data-string-dev-color-shell] {
      display: grid;
      align-items: center;
      width: 104px;
      max-width: 100%;
      min-height: 100%;
      border: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
      overflow: hidden;
      box-sizing: border-box;
      justify-self: end;
      position: relative;
    }

    [data-string-dev-select-shell] {
      grid-template-columns: minmax(0, 1fr) 32px;
    }

    [data-string-dev-color-shell] {
      grid-template-columns: 38px minmax(0, auto);
      column-gap: 0;
    }

    [data-string-dev-select-value-wrap] {
      min-width: 0;
      padding: 0 8px;
      pointer-events: none;
    }

    [data-string-dev-select-display] {
      display: block;
      width: 100%;
      font-size: 12px;
      font-family: var(--string-dev-panel-font-family);
      color: #111111;
      line-height: calc(var(--string-dev-input-height) - 4px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    [data-string-dev-select-shell] [data-string-dev-field-select] {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      opacity: 0;
      border: 0;
      outline: none;
      appearance: none;
      -webkit-appearance: none;
      cursor: pointer;
    }

    [data-string-dev-select-indicator] {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      min-height: 100%;
      border-left: 1px solid rgba(17, 17, 17, 0.15);
      color: rgba(17, 17, 17, 0.72);
      pointer-events: none;
    }

    [data-string-dev-select-indicator] svg {
      width: 14px;
      height: 14px;
    }

    [data-string-dev-color-swatch-wrap] {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      min-height: 100%;
      border-right: 1px solid rgba(17, 17, 17, 0.15);
      pointer-events: none;
    }

    [data-string-dev-color-input] {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      padding: 0;
      cursor: pointer;
      border: 0;
    }

    [data-string-dev-color-swatch] {
      width: 16px;
      height: 16px;
      border-radius: 999px;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.96);
    }

    [data-string-dev-color-value] {
      min-width: 65px;
      padding: 0 0 0 8px;
      font-size: 12px;
      font-family: var(--string-dev-panel-font-family);
      font-weight: 500;
      color: var(--string-dev-panel-text);
      text-align: left;
      pointer-events: none;
    }

    [data-string-dev-toggle-input] {
      display: none;
    }

    [data-string-dev-toggle] {
      width: 22px;
      height: 14px;
      border-radius: 999px;
      background: rgba(17, 17, 17, 0.18);
      display: inline-flex;
      align-items: center;
      position: relative;
      cursor: pointer;
    }

    [data-string-dev-toggle][data-checked="true"] {
      background: var(--string-dev-accent);
    }

    [data-string-dev-toggle-knob] {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #ffffff;
      position: absolute;
      top: 2px;
      left: 2px;
      transition: left 0.2s ease;
    }

    [data-string-dev-toggle][data-checked="true"] [data-string-dev-toggle-knob] {
      left: 10px;
    }

    [data-string-dev-divider] {
      height: 4px;
    }

    [data-string-dev-divider-label] {
      display: none;
      font-family: var(--string-dev-panel-font-family);
      color: var(--string-dev-panel-subtle);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  `;
}

let _injected = false;

export function ensureStringDevtoolsFieldStyles(): void {
  if (_injected) return;
  _injected = true;

  const style = document.createElement("style");
  style.id = "string-dev-ui-fields-styles";
  style.textContent = buildDevtoolsFieldsCSS();
  document.head.appendChild(style);
}
